/**
 * 底部标签栏。
 *
 * 形态是悬浮玻璃胶囊，不是传统的贴底通栏。理由有两个：
 *   · 玻璃胶囊在现代 iOS 里是主导航的形态，比通栏更"现在"
 *   · 悬浮之后，内容可以滚到它下面，视觉上页面更通
 *
 * 选中态用三件事共同表达：图标从描边变填充、文字变深、背后有一个滑动的玻璃胶囊。
 * 滑动胶囊只改 xPercent，不动 width，避免每帧触发布局。
 */

import { useSlidingPill } from '../motion';
import { ROUTES, type RouteKey } from '../router';
import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';

interface TabBarProps {
	current: RouteKey;
	onChange: (next: RouteKey) => void;
	/** 角标，例如待买清单里未勾选的数量 */
	badges?: Partial<Record<RouteKey, number>>;
	/**
	 * 正在后台干活的那一栏（目前只有"生成菜谱"会用）。
	 *
	 * 为什么要在导航上留个记号：生成是可以离开页面等的，用户切走之后
	 * 总得有个地方告诉他"还在跑、没白点"。不然他会怀疑自己那一下有没有生效，
	 * 又跑回去点一次。
	 */
	working?: RouteKey | null;
}

export function TabBar({
	current,
	onChange,
	badges = {},
	working = null,
}: TabBarProps): JSX.Element {
	const activeIndex = Math.max(
		0,
		ROUTES.findIndex((route) => route.key === current),
	);
	const pillRef = useSlidingPill<HTMLSpanElement>(activeIndex);

	return (
		<nav
			aria-label="主导航"
			className="absolute inset-x-0 bottom-0 z-30 px-4 pb-[max(0.625rem,env(safe-area-inset-bottom))]"
		>
			<div className="glass relative mx-auto flex max-w-[26rem] rounded-control border border-separator p-1 shadow-e2">
				{/* 滑动胶囊 */}
				<span
					ref={pillRef}
					aria-hidden
					className="pointer-events-none absolute bottom-1 top-1 left-1 rounded-control bg-label/10 will-change-transform"
					style={{ width: `calc((100% - 0.5rem) / ${ROUTES.length})` }}
				/>

				{ROUTES.map((route) => {
					const active = route.key === current;
					const badge = badges[route.key] ?? 0;
					const isWorking = working === route.key;
					return (
						<Pressable
							key={route.key}
							aria-current={active ? 'page' : undefined}
							onClick={() => onChange(route.key)}
							scale={0.92}
							className="relative z-10 flex flex-1 flex-col items-center gap-0.5 rounded-control py-1.5"
						>
							<span className="relative">
								<IconGlyph
									glyph={GLYPHS[route.key]}
									size={22}
									weight={active ? 'fill' : 'regular'}
									className={active ? 'text-label' : 'text-label-2'}
								/>
								{badge > 0 && (
									<span className="tnum absolute -right-2.5 -top-1 min-w-[17px] rounded-full bg-danger px-1 text-center text-[10px] font-semibold leading-[17px] text-white">
										{badge > 99 ? '99+' : badge}
									</span>
								)}
								{/* 干活中的点放在左上方：右上是角标的地盘，两者可能同时出现 */}
								{isWorking && (
									<span
										aria-hidden
										className="working-dot absolute -left-1.5 -top-0.5 h-[7px] w-[7px] rounded-full"
									/>
								)}
							</span>
							<span
								className={`text-[10.5px] leading-none ${
									active ? 'font-medium text-label' : 'text-label-2'
								}`}
							>
								{route.label}
							</span>
						</Pressable>
					);
				})}
			</div>
		</nav>
	);
}
